/**
 * Utility that can extract text out of a file blob.
 * Assuming that the fileblob contains ascii text.
 *
 * Usage, together with the module "key-parser":
 *
 * const selectedFileChanged = () => {
 *    const files = this._inputNode.files;
 *    const file = files[0]; // file === blob
 *    const linesPromise = convertToArray(file);
 *    linesPromise.then(lines => keyParser.parseGen2Dump(lines, progressFn))
 *                .then(keys => {
 *                    console.warn(keys);
 *                });
 * };
 *
 * <input multiple={false}
 *        name="uploadfiles"
 *        onChange={selectedFileChanged}
 *        ref={node => _inputNode = node}
 *        type="file" />
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module file-to-text.js
 * @class FileToText
 * @since 0.0.1
*/

/**
 * Extract text out of a file blob.
 * Assuming that the fileblob contains ascii text.
 *
 * @method convertToString
 * @param fileBlob {Object} the fileblob, given by a file selector
 * @return {String} the text content of the file.
 * @since 0.0.1
 */
const convertToString = fileBlob => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        // This fires after the blob has been read/loaded.
        const onLoadEnd = e => {
            const text = e.srcElement.result;
            resolve(text);
            reader.removeEventListener('loadend', onLoadEnd);
        };
        reader.addEventListener('loadend', onLoadEnd);

        // Start reading the blob as text.
        try {
            reader.readAsText(fileBlob);
        }
        catch (err) {
            reject(err);
        }
    });
};

/**
 * Extract text out of a file blob and returns every line into a single array element.
 * Assuming that the fileblob contains ascii text.
 *
 * @method convertToArray
 * @param fileBlob {Object} the fileblob, given by a file selector
 * @return {Array} the text content of the file, every line bacomes a single array element.
 * @since 0.0.1
 */
const convertToArray = fileBlob => {
    return convertToString(fileBlob).then(text => text.split(/\r\n?|\n/));
};

module.exports = {
    convertToString,
    convertToArray
};
